import React, { useState, useEffect, useRef, useCallback, lazy, Suspense } from 'react';
import './App.css';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider, useLang } from './context/LanguageContext';
import { FavoritesProvider } from './context/FavoritesContext';
import Header from './components/Header';
import { Capacitor } from '@capacitor/core';

// На mobile (iOS/Android) не показваме продажба на PRO — само web.
// App Store / Play Store съответствие (Apple 3.1.1 / Play Billing).
const IS_NATIVE = Capacitor.isNativePlatform();

// Lazy load тежки компоненти
const LandingPage = lazy(() => import('./components/LandingPage'));
const CompetitionSelector = lazy(() => import('./components/CompetitionSelector'));
const PredictionPanel = lazy(() => import('./components/PredictionPanel'));
const Dashboard = lazy(() => import('./components/Dashboard'));
const FavoriteMatches = lazy(() => import('./components/FavoriteMatches'));
const PricingPage = lazy(() => import('./components/PricingPage'));
const LegalPage = lazy(() => import('./components/LegalPage'));
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const DeleteAccountPage = lazy(() => import('./components/DeleteAccountPage'));



const setupStatusBar = async () => {
  if (Capacitor.isNativePlatform()) {
    try {
      const { StatusBar, Style } = await import('@capacitor/status-bar');
      await StatusBar.setOverlaysWebView({ overlay: true });
      await StatusBar.setStyle({ style: Style.Dark });
      const info = await StatusBar.getInfo();
      document.documentElement.style.setProperty(
        '--safe-top', `${info.height || 24}px`
      );
    } catch (e) {
      console.warn('StatusBar not available:', e);
      document.documentElement.style.setProperty('--safe-top', '24px');
    }
  } else {
    // Не задаваме --safe-top — оставяме env(safe-area-inset-top) да работи
  }
};
// Theme hook — auto-detects system preference, allows manual toggle
function useTheme() {
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem('prednex-theme');
    if (saved) return saved;
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('prednex-theme', theme);
  }, [theme]);

  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => {
      const saved = localStorage.getItem('prednex-theme');
      if (!saved) {
        setTheme(e.matches ? 'dark' : 'light');
      }
    };
    media.addEventListener('change', handler);
    return () => media.removeEventListener('change', handler);
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  }, []);

  return { theme, toggleTheme };
}

function AppContent() {
  const { user, loading } = useAuth();
  const { t } = useLang();

  useEffect(() => {
    setupStatusBar();
  }, []);

  if (loading) {
    return (
      <div className="app">
        <main className="loading-screen">
          <img src="/PREDNEXLOGO.svg" alt="Prednex" height="60" />
          <p>{t('loadingText')}</p>
        </main>
      </div>
    );
  }

  const LazyFallback = () => (
    <div className="app">
      <main className="loading-screen">
        <img src="/PREDNEXLOGO.svg" alt="Prednex" height="60" />
      </main>
    </div>
  );

  // Privacy Policy и Terms — директни URL-и за App Store / Play Store изисквания.
  // Тези страници трябва да са достъпни без логин (Apple/Google review проверяват).
  const pathname = window.location.pathname;
  if (pathname === '/privacy' || pathname === '/terms' || pathname === '/cookies') {
    const tab = pathname.slice(1); // 'privacy', 'terms', или 'cookies'
    return <Suspense fallback={<LazyFallback />}><LegalPage standalone initialTab={tab} /></Suspense>;
  }
  if (pathname === '/delete-account') {
    return <Suspense fallback={<LazyFallback />}><DeleteAccountPage /></Suspense>;
  }
  // Admin dashboard — отделна страница
  if (window.location.pathname === '/admin') {
    return <Suspense fallback={<LazyFallback />}><AdminDashboard /></Suspense>;
  }

  if (!user) {
    return <Suspense fallback={<LazyFallback />}><LandingPage /></Suspense>;
  }

  return <Suspense fallback={<LazyFallback />}><MainApp /></Suspense>;
}

function MainApp() {
  const { getAuthHeader, isPro, predictionsLeft, refreshPlan } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { t, lang } = useLang();

  const [competitions, setCompetitions] = useState([]);
  const [selectedCompetition, setSelectedCompetition] = useState(null);
  const [matches, setMatches] = useState([]);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [predictions, setPredictions] = useState(null);
  const [loadingData, setLoadingData] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
  const [showPricing, setShowPricing] = useState(false);
  const [showLegal, setShowLegal] = useState(null);
  const [view, setView] = useState('dashboard'); // 'dashboard' | 'leagues'
  const [matchSource, setMatchSource] = useState('dashboard'); // tracks where match was selected
  const [customParams, setCustomParams] = useState({
    homeCoachRating: 5,
    awayCoachRating: 5,
    refereeStrictness: 5
  });
  const predictionRef = useRef(null);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    async function loadCompetitions() {
      try {
        const r = await fetch(`${API_URL}/competitions`);
        const data = await r.json();
        setCompetitions(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error('Load competitions error:', e);
        setCompetitions([]);
      }
    }
    loadCompetitions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedCompetition) return;
    setLoadingData(true);
    setMatches([]);
    setSelectedMatch(null);
    setPredictions(null);

    async function loadMatches() {
      try {
        const headers = await getAuthHeader();
        const r = await fetch(`${API_URL}/matches/${selectedCompetition.id}`, { headers });
        const data = await r.json();
        setMatches(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoadingData(false);
      }
    }
    loadMatches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompetition]);

  const handleMatchSelect = (match) => {
    setSelectedMatch({ ...match, competitionId: selectedCompetition?.id });
    setPredictions(null);
    setMatchSource('leagues');
    if (window.innerWidth <= 768) {
      setTimeout(() => window.scrollTo(0, 0), 50);
    }
  };

  const handleBackToMatches = () => {
    setSelectedMatch(null);
    setPredictions(null);
    setView(matchSource);
  };

  const handleGoHome = () => {
    setSelectedCompetition(null);
    setSelectedMatch(null);
    setMatches([]);
    setPredictions(null);
    setView('dashboard');
  };

  const handleDashboardMatchSelect = (match, league) => {
    setSelectedMatch({ ...match, competitionId: league.id, competition: league.name });
    setPredictions(null);
    setMatchSource('dashboard');
    if (window.innerWidth <= 768) {
      setTimeout(() => window.scrollTo(0, 0), 50);
    }
  };

  const handlePredictMatch = async () => {
    if (!selectedMatch) return;

    if (!isPro && predictionsLeft <= 0) {
      if (IS_NATIVE) {
        alert(lang === 'bg'
          ? 'Достигнахте дневния лимит от 3 безплатни прогнози. Опитайте отново утре.'
          : 'You have reached the daily limit of 3 free predictions. Try again tomorrow.');
      } else {
        alert(t('dailyLimitReached'));
      }
      return;
    }

    setLoadingData(true);
    try {
      const headers = await getAuthHeader();
      const r = await fetch(`${API_URL}/predict`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({
          homeTeamId: selectedMatch.homeTeam.id,
          homeTeamName: selectedMatch.homeTeam.name,
          awayTeamId: selectedMatch.awayTeam.id,
          awayTeamName: selectedMatch.awayTeam.name,
          fixtureId: selectedMatch.id,
          leagueId: selectedMatch.competitionId || selectedCompetition?.id,
          matchDate: selectedMatch.utcDate,
          lang,
          ...customParams
        })
      });

      if (r.status === 429) {
        const data = await r.json();
        alert(data.error || t('dailyLimitError'));
        return;
      }

      if (r.status === 403) {
        alert(t('proOnlyLeague'));
        return;
      }

      const data = await r.json();
      if (!data.error) {
        setPredictions(data);
      } else {
        console.error('Prediction error:', data.error);
      }
    } catch (e) {
      console.error('Error generating prediction:', e);
    } finally {
      setLoadingData(false);
    }

    if (refreshPlan) refreshPlan();
  };

  const showLeftPanel = !isMobile || !selectedMatch;
  const showRightPanel = !isMobile || selectedMatch;

  // Expose for PredictionPanel disclaimer link
  window.__showLegal = (tab) => setShowLegal(tab);

  return (
    <div className="app">
      <Header
        theme={theme}
        toggleTheme={toggleTheme}
        onShowPricing={() => setShowPricing(true)}
        onGoHome={handleGoHome}
      />

      <main className="container">
        {showLeftPanel && (
          <div className="left-panel">
            <div className="view-tabs">
              <button
                className={`view-tab ${view === 'dashboard' ? 'active' : ''}`}
                onClick={() => setView('dashboard')}
              >
                {lang === 'bg' ? '📋 Обзор' : '📋 Overview'}
              </button>
              <button
                className={`view-tab ${view === 'leagues' ? 'active' : ''}`}
                onClick={() => setView('leagues')}
              >
                {lang === 'bg' ? '🏆 Лиги' : '🏆 Leagues'}
              </button>
              <button
                className={`view-tab ${view === 'favorites' ? 'active' : ''}`}
                onClick={() => setView('favorites')}
              >
                {lang === 'bg' ? '⭐ Любими' : '⭐ Favorites'}
              </button>
            </div>

            {view === 'dashboard' ? (
              <Dashboard onSelectMatch={handleDashboardMatchSelect} />
            ) : view === 'favorites' ? (
              <FavoriteMatches onSelectMatch={handleDashboardMatchSelect} />
            ) : (
              <CompetitionSelector
              competitions={competitions}
              selectedCompetition={selectedCompetition}
              onSelectCompetition={setSelectedCompetition}
              loading={loadingData}
              matches={matches}
              selectedMatch={selectedMatch}
              onSelectMatch={handleMatchSelect}
            />
            )}
          </div>
        )}

        {showRightPanel && selectedMatch && (
          <div className="right-panel" ref={predictionRef}>
            {isMobile && (
              <button className="back-btn" onClick={handleBackToMatches}>
                {t('backToMatches')}
              </button>
            )}
            <PredictionPanel
              match={selectedMatch}
              predictions={predictions}
              customParams={customParams}
              onCustomParamsChange={setCustomParams}
              onPredict={handlePredictMatch}
              loading={loadingData}
            />
          </div>
        )}
      </main>

      <footer className="app-footer">
        <button onClick={() => setShowLegal('terms')}>
          {t('home') === 'Начало' ? 'Условия' : 'Terms'}
        </button>
        <span>·</span>
        <button onClick={() => setShowLegal('privacy')}>
          {t('home') === 'Начало' ? 'Поверителност' : 'Privacy'}
        </button>
        <span>·</span>
        <button onClick={() => setShowLegal('cookies')}>
          {t('home') === 'Начало' ? 'Бисквитки' : 'Cookies'}
        </button>
        <span>·</span>
        <span className="footer-copy">© 2026 Prednex</span>
      </footer>

      {showPricing && <PricingPage onClose={() => setShowPricing(false)} />}
      {showLegal && <LegalPage onClose={() => setShowLegal(null)} initialTab={showLegal} />}
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <LanguageProvider>
        <FavoritesProvider>
          <AppContent />
        </FavoritesProvider>
      </LanguageProvider>
    </AuthProvider>
  );
}
